import { Action } from '@appTypes/Action';
import { CustomerProfileModalResource } from '@appTypes/CustomerProfileModalResource';
import {
  IBpsScoreType,
  IDealsheetData,
  IDealsheetTab,
  ILegacyDealsheetData,
  MultiVehicleComparisonData,
  MultiVehicleComparisonOverrides
} from '@appTypes/DealsheetTypes';
import { InventoryResource } from '@appTypes/InvetoryResource';
import { CarfaxConnectUseCase } from '@enums/CarfaxConnectUseCase';

export enum DealsheetActionType {
  SET_LEGACY_DEALSHEET_DATA = 'SET_LEGACY_DEALSHEET_DATA',
  SET_DEALSHEET_DATA = 'SET_DEALSHEET_DATA',
  SET_IS_FETCHING_DATA = 'SET_IS_FETCHING_DATA',
  SET_HAS_TEMPORARY_REPLACEMENT = 'SET_HAS_TEMPORARY_REPLACEMENT',
  SET_TEMPORARY_REPLACEMENT = 'SET_TEMPORARY_REPLACEMENT',
  SET_OFFER_ID = 'SET_OFFER_ID',
  SET_SELECTED_BPS_SCORE_TYPE = 'SET_SELECTED_BPS_SCORE_TYPE',
  SET_IS_MULTI_VEHICLE_COMPARISON_PAGE_OPEN = 'SET_IS_MULTI_VEHICLE_COMPARISON_PAGE_OPEN',
  SET_IS_COMPARE_PRODUCTS_PAGE_OPEN = 'SET_IS_COMPARE_PRODUCTS_PAGE_OPEN',
  SET_IS_HISTORY_OPEN = 'SET_IS_HISTORY_OPEN',
  SET_IS_MOBILE_HAMBURGER_NAV_OPEN = 'SET_IS_MOBILE_HAMBURGER_NAV_OPEN',
  SET_IS_CUSTOMER_PROFILE_OPEN = 'SET_IS_CUSTOMER_PROFILE_OPEN',
  SET_IS_LOYALTY_OPEN = 'SET_IS_LOYALTY_OPEN',
  SET_IS_NOTES_OPEN = 'SET_IS_NOTES_OPEN',
  SET_DEALSHEET_ACTIVE_TAB = 'SET_DEALSHEET_ACTIVE_TAB',
  SET_DEAL_TYPE = 'SET_DEAL_TYPE',
  SET_CUSTOMER_PROFILE_DATA = 'SET_CUSTOMER_PROFILE_DATA',
  SET_IS_RECALL_MODAL_OPEN = 'SET_IS_RECALL_MODAL_OPEN',
  SET_IS_INVENTORY_BROWSER_MODAL_OPEN = 'SET_IS_INVENTORY_BROWSER_MODAL_OPEN',
  SET_CARFAX_SNAPSHOT_KEY = '',
  SET_CARFAX_SNAPSHOT_KEYS = 'SET_CARFAX_SNAPSHOT_KEYS',
  SET_CARFAX_SNAPSHOT_KEY_ERROR_MESSAGES = 'SET_CARFAX_SNAPSHOT_KEY_ERROR_MESSAGES',
  SET_DEALSHEET_PAGINATION = 'SET_DEALSHEET_PAGINATION',
  SET_SELECTED_INVENTORY_VEHICLE = 'SET_SELECTED_INVENTORY_VEHICLE',
  SET_TRADE_VALUE = 'SET_TRADE_VALUE',
  SET_PAYMENT_TYPE = 'SET_PAYMENT_TYPE',
  SET_PRIMARY_AI_MESSAGE = 'SET_PRIMARY_AI_MESSAGE',
  SET_SECONDARY_AI_MESSAGE = 'SET_SECONDARY_AI_MESSAGE',
  SET_REPLACEMENT_AI_MESSAGE = 'SET_REPLACEMENT_AI_MESSAGE',
  RESET_REPLACEMENT_AI_MESSAGE = 'RESET_REPLACEMENT_AI_MESSAGE',
  RESET_PRIMARY_AI_MESSAGE = 'RESET_PRIMARY_AI_MESSAGE',
  RESET_SECONDARY_AI_MESSAGE = 'RESET_SECONDARY_AI_MESSAGE',
  RESET_ALL_MESSAGES = 'RESET_ALL_MESSAGES',
  ADD_PRIMARY_AI_MESSAGE = 'ADD_PRIMARY_AI_MESSAGE',
  ADD_SECONDARY_AI_MESSAGE = 'ADD_SECONDARY_AI_MESSAGE',
  ADD_REPLACEMENT_AI_MESSAGE = 'ADD_REPLACEMENT_AI_MESSAGE',
  SET_IS_LOADING = 'SET_IS_LOADING',
  SET_IS_DEALSHEET_RELOAD = 'SET_IS_DEALSHEET_RELOAD',
  SET_CURRENT_STORE_NAME = 'SET_CURRENT_STORE_NAME',
  SET_SELECTED_VEHICLE_ID = 'SET_SELECTED_VEHICLE_ID',
  SET_MULTI_VEHICLE_COMPARISON_DATA = 'SET_MULTI_VEHICLE_COMPARISON_DATA',
  SET_MULTI_VEHICLE_COMPARISON_OVERRIDES = 'SET_MULTI_VEHICLE_COMPARISON_OVERRIDES'
}

export interface SetSelectedVehicleIdAction extends Action {
  type: DealsheetActionType.SET_SELECTED_VEHICLE_ID;
  payload: string | undefined;
}

export const setLegacyDealsheetData = (value: ILegacyDealsheetData): Action => ({
  type: DealsheetActionType.SET_LEGACY_DEALSHEET_DATA,
  payload: value,
});

export const setDealsheetData = (value: Array<IDealsheetData>): Action => ({
  type: DealsheetActionType.SET_DEALSHEET_DATA,
  payload: value,
});

export const setDealsheetPagination = (
  value: Array<{ name: string; bps: string; vin: string; dealerId: string; dealType: string }>,
): Action => ({
  type: DealsheetActionType.SET_DEALSHEET_PAGINATION,
  payload: value,
});

export const setTemporaryReplacement = (value: InventoryResource | null): Action => ({
  type: DealsheetActionType.SET_TEMPORARY_REPLACEMENT,
  payload: value,
});

export const setIsFetchingData = (value: boolean): Action => ({
  type: DealsheetActionType.SET_IS_FETCHING_DATA,
  payload: value,
});

export const setOfferId = (value: string): Action => ({
  type: DealsheetActionType.SET_OFFER_ID,
  payload: value,
});

export const setDealType = (value: string): Action => ({
  type: DealsheetActionType.SET_DEAL_TYPE,
  payload: value,
});

export const setSelectedBpsScoreType = (value: IBpsScoreType): Action => ({
  type: DealsheetActionType.SET_SELECTED_BPS_SCORE_TYPE,
  payload: value,
});

export const setIsMultiVehicleComparisonPageOpen = (value: boolean): Action => ({
  type: DealsheetActionType.SET_IS_MULTI_VEHICLE_COMPARISON_PAGE_OPEN,
  payload: value,
});

export const setIsCompareProductsPageOpen = (value: boolean): Action => ({
  type: DealsheetActionType.SET_IS_COMPARE_PRODUCTS_PAGE_OPEN,
  payload: value,
});

export const setIsHistoryOpen = (value: boolean): Action => ({
  type: DealsheetActionType.SET_IS_HISTORY_OPEN,
  payload: value,
});

export const setIsMobileHamburgerNavOpen = (value: boolean): Action => ({
  type: DealsheetActionType.SET_IS_MOBILE_HAMBURGER_NAV_OPEN,
  payload: value,
});

export const setIsCustomerProfileOpen = (value: boolean): Action => ({
  type: DealsheetActionType.SET_IS_CUSTOMER_PROFILE_OPEN,
  payload: value,
});

export const setIsLoyaltyOpen = (value: boolean): Action => ({
  type: DealsheetActionType.SET_IS_LOYALTY_OPEN,
  payload: value,
});

export const setIsNotesOpen = (value: boolean): Action => ({
  type: DealsheetActionType.SET_IS_NOTES_OPEN,
  payload: value,
});

export const setDealsheetActiveTab = (value: IDealsheetTab): Action => ({
  type: DealsheetActionType.SET_DEALSHEET_ACTIVE_TAB,
  payload: value,
});

export const setCustomerProfileData = (
  customerProfileModalResource: CustomerProfileModalResource,
): Action => ({
  type: DealsheetActionType.SET_CUSTOMER_PROFILE_DATA,
  payload: customerProfileModalResource,
});

export const setIsRecallModalOpen = (value: boolean): Action => ({
  type: DealsheetActionType.SET_IS_RECALL_MODAL_OPEN,
  payload: value,
});

export const setIsInventoryBrowserModalOpen = (value: boolean): Action => ({
  type: DealsheetActionType.SET_IS_INVENTORY_BROWSER_MODAL_OPEN,
  payload: value,
});

export const setInventorySelectedVehicle = (value: any): Action => ({
  type: DealsheetActionType.SET_SELECTED_INVENTORY_VEHICLE,
  payload: value,
});

export const setCarFaxSnapshotKey = (value: string) => ({
  type: DealsheetActionType.SET_CARFAX_SNAPSHOT_KEY,
  payload: value,
});

export const setCarFaxSnapshotKeys = (
  value: Array<{ name: CarfaxConnectUseCase; key: string }>,
) => ({
  type: DealsheetActionType.SET_CARFAX_SNAPSHOT_KEYS,
  payload: value,
});

export const setCarFaxSnapshotKeyErrorMessages = (
  value: Array<{ name: CarfaxConnectUseCase; message: string }>,
) => ({
  type: DealsheetActionType.SET_CARFAX_SNAPSHOT_KEY_ERROR_MESSAGES,
  payload: value,
});

export const setTradeValue = (value: number) => ({
  type: DealsheetActionType.SET_TRADE_VALUE,
  payload: value,
});

export const setPaymentType = (value: string) => ({
  type: DealsheetActionType.SET_PAYMENT_TYPE,
  payload: value,
});

export const setPrimaryAiMessage = (messages: any[]): Action => ({
  type: DealsheetActionType.SET_PRIMARY_AI_MESSAGE,
  payload: messages,
});

export const setSecondaryAiMessage = (messages: any[]): Action => ({
  type: DealsheetActionType.SET_SECONDARY_AI_MESSAGE,
  payload: messages,
});

export const setReplacementAiMessage = (messages: any[]): Action => ({
  type: DealsheetActionType.SET_REPLACEMENT_AI_MESSAGE,
  payload: messages,
});

export const resetAllAiMessages = (): Action => ({
  type: DealsheetActionType.RESET_ALL_MESSAGES,
});

export const resetReplacementAiMessages = (): Action => ({
  type: DealsheetActionType.RESET_REPLACEMENT_AI_MESSAGE,
});

export const resetPrimaryAiMessages = (): Action => ({
  type: DealsheetActionType.RESET_PRIMARY_AI_MESSAGE,
});

export const resetSecondaryAiMessages = (): Action => ({
  type: DealsheetActionType.RESET_SECONDARY_AI_MESSAGE,
});

export const addPrimaryAiMessage = (message: any): Action => ({
  type: DealsheetActionType.ADD_PRIMARY_AI_MESSAGE,
  payload: message,
});

export const addSecondaryAiMessage = (message: any): Action => ({
  type: DealsheetActionType.ADD_SECONDARY_AI_MESSAGE,
  payload: message,
});

export const addReplacementAiMessage = (message: any): Action => ({
  type: DealsheetActionType.ADD_REPLACEMENT_AI_MESSAGE,
  payload: message,
});

export const setAiIsLoading = (isLoading: boolean): Action => ({
  type: DealsheetActionType.SET_IS_LOADING,
  payload: isLoading,
});

export const setIsDealsheetReload = (value: boolean): Action => ({
  type: DealsheetActionType.SET_IS_DEALSHEET_RELOAD,
  payload: value,
});

export const setSelectedVehicleId = (vehicleId: string | undefined): SetSelectedVehicleIdAction => {
  return {
    type: DealsheetActionType.SET_SELECTED_VEHICLE_ID,
    payload: vehicleId,
  };
};

export const setMultiVehicleComparisonData = (value: MultiVehicleComparisonData): Action => ({
  type: DealsheetActionType.SET_MULTI_VEHICLE_COMPARISON_DATA,
  payload: value
});

export const setMultiVehicleComparisonOverrides = (value: MultiVehicleComparisonOverrides): Action => ({
  type: DealsheetActionType.SET_MULTI_VEHICLE_COMPARISON_OVERRIDES,
  payload: value
});

export type DealsheetActions = ReturnType<
  | typeof setLegacyDealsheetData
  | typeof setDealsheetData
  | typeof setIsFetchingData
  | typeof setOfferId
  | typeof setDealType
  | typeof setSelectedBpsScoreType
  | typeof setIsMultiVehicleComparisonPageOpen
  | typeof setIsCompareProductsPageOpen
  | typeof setIsHistoryOpen
  | typeof setIsMobileHamburgerNavOpen
  | typeof setIsCustomerProfileOpen
  | typeof setIsLoyaltyOpen
  | typeof setIsNotesOpen
  | typeof setDealsheetActiveTab
  | typeof setCustomerProfileData
  | typeof setIsRecallModalOpen
  | typeof setIsInventoryBrowserModalOpen
  | typeof setTemporaryReplacement
  | typeof setCarFaxSnapshotKey
  | typeof setCarFaxSnapshotKeys
  | typeof setCarFaxSnapshotKeyErrorMessages
  | typeof setDealsheetPagination
  | typeof setInventorySelectedVehicle
  | typeof setTradeValue
  | typeof setPaymentType
  | typeof setSecondaryAiMessage
  | typeof setReplacementAiMessage
  | typeof resetAllAiMessages
  | typeof resetReplacementAiMessages
  | typeof resetPrimaryAiMessages
  | typeof resetSecondaryAiMessages
  | typeof addPrimaryAiMessage
  | typeof addSecondaryAiMessage
  | typeof addReplacementAiMessage
  | typeof setAiIsLoading
  | typeof setIsDealsheetReload
  | typeof setSelectedVehicleId
  | typeof setMultiVehicleComparisonData
  | typeof setMultiVehicleComparisonOverrides
>;
